/*
 * Categories Store
 * */
import { defineStore } from 'pinia';
import { ref, reactive, computed } from 'vue';
import { pathOr, hasPath } from 'ramda';
import { useAuthStore } from '@/store/AuthStore';
import { useCollection } from 'vuefire';
import { doc, setDoc, getDoc, collection, deleteDoc } from 'firebase/firestore';
import { fbDb } from '@/firebaseApp';
import { useToast } from 'vue-toastification';
import { useApi } from '@/hooks/useApi';
import { getUUID, pathPut } from '@enegelai/core';
import { CAT_TYPE_DEFS } from '@/spec/CategoriesTypeDefs';

const EMPTY_CAT_SPEC = {
  name: 'New Categories',
};

export const useCategoriesStore = defineStore('categories', () => {
  const authStore = useAuthStore();

  const tenantId = computed(() => authStore.tenantId);

  const api = useApi();

  // List of all categories sets, with subscription - will be updated if any document is changed in the collection
  const categoriesCollection = useCollection(collection(fbDb, `tenants/${tenantId.value}/categories`));

  const catIdAssigned = ref(null);

  const catIsNew = ref(false);
  const catIsChanged = ref(false);

  const catSpec = ref(reactive({}));
  const catId = computed(() => catSpec.value?.id || null);
  const catName = computed(() => catSpec.value?.name || null);
  const catType = computed(() => catSpec.value?.type || null);
  const catTags = computed(() => catSpec.value?.tags || []);
  const catKpiCollection = computed(() => catSpec.value?.kpiCollection || null);
  const catKpiCollectionRef = computed(() => (catKpiCollection.value ? collection(fbDb, `tenants/${tenantId.value}/kpi/${catKpiCollection.value}/docs`) : null));
  const catKpis = useCollection(catKpiCollectionRef);

  const catTypeTitle = computed(() => {
    return catType.value in CAT_TYPE_DEFS ? CAT_TYPE_DEFS[catType.value].title : 'UNKNOWN';
  });

  const catIcon = computed(() => {
    return catType.value in CAT_TYPE_DEFS ? CAT_TYPE_DEFS[catType.value].icon : 'message-processing-outline';
  });

  const toast = useToast();

  function getCat(catId) {
    const cat = categoriesCollection.value.find((x) => x.id === catId);
    return cat ? cat : null;
  }

  // set ID to categories recursively if missing
  function ensureCategoriesIds(categories) {
    if (!Array.isArray(categories) || categories.length === 0) {
      return;
    }
    categories.forEach((cat) => {
      if (!('id' in cat) || !cat.id) {
        cat.id = getUUID();
      }
      if ('categories' in cat) {
        ensureCategoriesIds(cat.categories);
      }
    });
  }

  function normalizeCatSpec() {
    if (!('id' in catSpec.value) || !catSpec.value.id) {
      const id = getUUID();
      setCatSpecProp('id', id);
    }
    if (!('type' in catSpec.value) || !(catSpec.value.type in CAT_TYPE_DEFS)) {
      setCatSpecProp('type', 'standard');
    }
    const catType = catSpec.value?.type || 'standard';
    // Set defaults for all spec props
    const catTypeDef = CAT_TYPE_DEFS[catType] || null;
    if (catTypeDef) {
      const props = catTypeDef?.props || [];
      for (let i = 0; i < props.length; i++) {
        let pName = props[i]?.name;
        let pDef = props[i]?.default;
        // pName could be path in dot notation: prop.childprop ...
        const propPath = pName.split('.');
        const propExists = hasPath(propPath, catSpec.value);
        //console.log(`Checking: ${propPath}: exists: ${propValue !== null}`);
        if (!propExists) {
          setCatSpecProp(pName, pDef);
        }
      }
    }
    ensureCategoriesIds(catSpec.value.categories);
  }

  function setCatSpecProp(propPath, value) {
    catIsChanged.value = true;
    pathPut(catSpec.value, propPath, value);
  }

  // Can pass array or dot notation path, like "spec.bot.prop"
  function getCatSpecPropGetSet(propPath) {
    const pp = Array.isArray(propPath) ? propPath : propPath.split('.');
    return {
      get() {
        return pathOr(null, pp, catSpec.value);
      },
      set(newValue) {
        setCatSpecProp(pp, newValue);
      },
    };
  }

  async function createCat(catType, catName = 'New Categories') {
    const newCatSpec = {
      id: getUUID(),
      name: catName,
      type: catType,
    };
    catSpec.value = reactive(newCatSpec);
    normalizeCatSpec();
    catIsNew.value = true;
    catIsChanged.value = true;
    return newCatSpec.id;
  }

  // Should be called from Categories editor when we are already editing spec
  // Just updates name and ID
  function cloneCatInPlace(newName = '') {
    const newCatName = newName && newName !== '' ? newName : catName.value + ' (Copy)';
    const newCatId = getUUID();
    catSpec.value.id = newCatId;
    catSpec.value.name = newCatName;
    catIsNew.value = true;
    catIsChanged.value = true;
    return newCatId;
  }

  // TODO Revisit this - should be called from Cat list ?
  async function cloneCat(catSpec) {
    const newCatName = (catSpec?.name || 'New Categories') + ' (Copy)';
    const newCatSpec = Object.assign({}, JSON.parse(JSON.stringify(catSpec)), {
      id: getUUID(),
      name: newCatName,
    });
    catSpec.value = reactive(newCatSpec);
    normalizeCatSpec();
    catIsNew.value = true;
    catIsChanged.value = true;
    return newCatSpec.id;
  }

  async function importCat(newCatSpec) {
    catSpec.value = reactive(newCatSpec);
    normalizeCatSpec();
    catIsNew.value = true;
    catIsChanged.value = true;
    return catSpec.value.id;
  }

  // Load existing or create new empty cat document
  // Save id, so we know current kb id while loading spec
  async function loadCat(id) {
    // If this already loaded (i.e. newly created/imported cat), return right away
    if (catId.value === id) {
      return;
    }
    catIdAssigned.value = id;
    const emptySpec = Object.assign({ id: id }, EMPTY_CAT_SPEC);
    let currentCatSpec = null;
    const catDocRef = doc(fbDb, `tenants/${tenantId.value}/categories`, id);
    const catDoc = await getDoc(catDocRef);
    if (catDoc.exists()) {
      const catSpecDoc = await getDoc(doc(catDocRef, 'versions', 'current'));
      if (!catSpecDoc.exists()) {
        currentCatSpec = emptySpec;
        // TODO spec does not exit - make it out from what we know from the doc
      } else {
        const specData = catSpecDoc.data();
        let spec = null;
        try {
          spec = JSON.parse(specData.spec);
        } catch (e) {
          console.log(`CAT[${id}]: Failed to parse spec JSON: ${e.message}`);
          spec = emptySpec;
        }
        currentCatSpec = spec;
      }
    } else {
      console.log(`CAT[${id}]: Does not exist, assuming new`);
      currentCatSpec = emptySpec;
      catIsNew.value = true;
    }
    catSpec.value = reactive(currentCatSpec);
    catIsChanged.value = false;
    normalizeCatSpec();
  }

  // Saves currently loaded Categories
  async function saveCat() {
    const res = {
      success: false,
      error: '',
    };

    const catId = catSpec.value?.id || null;
    if (!catId) {
      res.error = 'Categories not loaded';
      return res;
    }

    const setRes = await api.setCategories({
      categoriesId: catId,
      categories: catSpec.value,
      updatedBy: authStore.displayName,
    });

    if (!setRes.success) {
      res.error = setRes.error;
      return res;
    }

    /*
    try {
      await setDoc(
        doc(fbDb, `tenants/${tenantId.value}/categories`, catId),
        {
          name: catSpec.value?.name || '',
          description: catSpec.value?.description || '',
          type: catSpec.value?.type || '',
          tags: catSpec.value?.tags || [],
          updated: Date.now(),
          updatedBy: authStore.displayName,
        },
        { merge: true },
      );
    } catch (e) {
      console.log(`CAT[${catId}]: Error writing document: ${e.message}`);
      res.error = e.message;
      return res;
    }

    const catSpecJSON = JSON.stringify(catSpec.value);
    try {
      await setDoc(doc(fbDb, `tenants/${tenantId.value}/categories/${catId}/versions`, 'current'), {
        spec: catSpecJSON,
      });
    } catch (e) {
      console.log(`CAT[${catId}]: Error writing spec: ${e.message}`);
      res.error = e.message;
      return res;
    }
    */

    catIsNew.value = false;
    catIsChanged.value = false;
    res.success = true;
    return res;
  }

  async function deleteCat(catId) {
    try {
      // TODO Delete versions collection ?
      await deleteDoc(doc(fbDb, `tenants/${tenantId.value}/categories`, catId));
    } catch (e) {
      console.log(`CAT[${catId}]: Error deleting CAT: ${e.message}`);
      return false;
    }
    return true;
  }

  function setChanged() {
    catIsChanged.value = true;
  }

  // ACTIONS // ////////////////////////////////////////////////

  // Called before saving Cat document.
  // Observers can subscribe and prepare document for saving - todo ???
  async function beforeSaveCatDocument() {
    console.log(`catStore: beforeSaveCatDocument executed`);
    return null;
  }

  return {
    categoriesCollection,
    catSpec,
    catId,
    catName,
    catType,
    catTypeTitle,
    catTags,
    catKpiCollection,
    catKpis,
    catIcon,
    catIsNew,
    catIsChanged,
    getCat,
    createCat,
    cloneCat,
    cloneCatInPlace,
    importCat,
    loadCat,
    setCatSpecProp,
    getCatSpecPropGetSet,
    saveCat,
    deleteCat,
    beforeSaveCatDocument,
    setChanged,
  };
});
